import { intentDoneWithin } from "./api";

export interface DraftOwner {
  readonly issuer: string;
  readonly tenant_id: string;
  readonly subject: string;
  readonly person?: { readonly person_id: string } | null;
}

const PREFIX = "riksu.working-draft.v1:";
const LEGACY_MESSAGE_KEY = "riksu.messaging.draft";
let generation = 0;

/** Same-origin storage is not an authority boundary. Server reads/writes still decide. */
export function workingDraftKey(owner: DraftOwner, kind: string): string {
  return (
    PREFIX +
    JSON.stringify([
      owner.issuer,
      owner.tenant_id,
      owner.subject,
      ...(kind === "message" ||
      kind === "service-request" ||
      kind.startsWith("interview-scorecard:") ||
      kind.startsWith("performance-review:")
        ? [owner.person?.person_id ?? null]
        : []),
      kind,
    ])
  );
}

export function draftGeneration(): number {
  return generation;
}

export function forgetWorkingDraft(key: string): void {
  removeWorkingCopy(key);
  intentDoneWithin(key);
}

export function removeWorkingCopy(key: string): boolean {
  try {
    sessionStorage.removeItem(key);
    return true;
  } catch {
    // Editing and server submission remain available when browser storage is blocked.
    return false;
  }
}

/** Native logout must stop pagehide from writing a just-cleared copy back. */
export function clearWorkingDrafts(): void {
  generation++;
  keepWorkingDraft(null);
  intentDoneWithin("report-template-import:");
}

/** Remove copies belonging to an earlier principal, tenant, issuer or lost licence. */
export function keepWorkingDraft(key: string | null): void {
  try {
    sessionStorage.removeItem(LEGACY_MESSAGE_KEY);
    const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i));
    for (const stored of keys) {
      if (stored?.startsWith(PREFIX) && stored !== key) sessionStorage.removeItem(stored);
    }
  } catch {
    // No claim of recovery when the storage API refuses access.
  }
  intentDoneWithin(PREFIX, key);
}

/** Keep only this verified owner's authorized form namespaces and uncertain intents. */
export function keepOwnedWorkingDrafts(
  owner: DraftOwner,
  requisition: boolean,
  interview: boolean,
  messaging = false,
  service = false,
  performance = false,
): void {
  const requisitionKey = workingDraftKey(owner, "requisition");
  const messageKey = workingDraftKey(owner, "message");
  const serviceKey = workingDraftKey(owner, "service-request");
  // JSON's final string quote and array bracket are removed from this fixed namespace.
  // The entire preceding issuer/tenant/subject tuple remains exactly encoded.
  const interviewPrefix = workingDraftKey(owner, "interview-scorecard:").slice(0, -2);
  const performancePrefix = workingDraftKey(owner, "performance-review:").slice(0, -2);
  const retained = (key: string) =>
    (requisition && (key === requisitionKey || key.startsWith(`${requisitionKey}:`))) ||
    (interview && key.startsWith(interviewPrefix)) ||
    (messaging && key === messageKey) ||
    (service && (key === serviceKey || key.startsWith(`${serviceKey}:`))) ||
    (performance && key.startsWith(performancePrefix));
  try {
    sessionStorage.removeItem(LEGACY_MESSAGE_KEY);
    const keys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i));
    for (const key of keys) {
      if (key?.startsWith(PREFIX) && !retained(key)) sessionStorage.removeItem(key);
    }
  } catch {
    // Same-origin storage may refuse access; no recovery claim follows.
  }
  intentDoneWithin(PREFIX, retained);
}

export type DraftValues = Record<string, string>;
export type DraftValidator = (value: unknown) => DraftValues | null;
export type DraftRead =
  | { readonly status: "ready"; readonly values: DraftValues | null }
  | { readonly status: "unavailable"; readonly values: null };
